import React, { useEffect, useState } from "react";
import { Box, Button, Card, Typography } from "@mui/material";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import NoOrders from "../../assets/myOrders/NoOrders.svg";
import Breadcrumbs from "../../common/Breadcrumbs";
import { fetchOrders } from "../../api/orderFinalAPI";
import DataTable from "../../common/DataTable";
import {
  columnStyles,
  EmptyContainer,
  EmptyOrdersSVG,
  actionButtonStyle,
} from "./MyOrders.styles";
import { CustomerOrder, Pagination } from "../../types/Order";
import { getUserEmailFromLocalStorage } from "../../utils/localStorageUtils";
import useIsMobile from "../../common/mobileVersion/useIsMobile";

const MyOrders: React.FC = () => {
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const orgId = localStorage.getItem("orgId");

  // Retrieve the email
  const emailId = getUserEmailFromLocalStorage() || "";

  if (!emailId) {
    console.warn("No email ID found in userRole key.");
  }

  useEffect(() => {
    if (!emailId) {
      navigate("/");
      return;
    }
    const loadOrders = async () => {
      try {
        const fetchedOrders = await fetchOrders({
          emailId,
          page,
          limit,
          search,
        });
        setOrders(fetchedOrders.orders);
        setPagination(fetchedOrders.pagination);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [emailId, page, limit, search]);

  const columns = [
    {
      label: "Date",
      field: "date",
      ...columnStyles,
      renderCell: (row: CustomerOrder) => (
        <Typography sx={columnStyles.cellStyle}>
          {moment(row.date).format("DD/MM/YYYY")}
        </Typography>
      ),
    },
    {
      label: "Order ID",
      field: "orderID",
      ...columnStyles,
      renderCell: (row: CustomerOrder) => (
        <Typography
          sx={{
            ...columnStyles.cellStyle,
            color: isMobile ? "#4C93FF" : "#585562",
            cursor: isMobile ? "pointer" : "default",
          }}
          onClick={() => {
            if (isMobile) {
              handleCheckDetails(row.orderID);
            }
          }}
        >
          #{row.orderID}
        </Typography>
      ),
    },
    {
      label: "Payment",
      field: "payment",
      ...columnStyles,
      renderCell: (row: CustomerOrder) => (
        <Typography
          sx={{
            ...columnStyles.cellStyle,
            color: row.payment === "On hold" ? "#FF0F00" : "#585562",
          }}
        >
          {row.payment}
        </Typography>
      ),
    },
    {
      label: "Order Status",
      field: "status",
      ...columnStyles,
      renderCell: (row: CustomerOrder) => (
        <Typography sx={{ ...columnStyles.cellStyle, color: "#585562" }}>
          {row.status}
        </Typography>
      ),
    },
    {
      label: "",
      field: "actions",
      labelStyle: {
        width: isMobile ? "80px" : "150px",
      },
      cellStyle: {
        width: isMobile ? "30px" : "150px",
        textAlign: "center" as const,
        margin: "200px",
      },

      renderCell: (row: CustomerOrder) => (
        <Button
          variant="contained"
          size="small"
          sx={actionButtonStyle(isMobile)}
          data-testid={`check-details-${row.orderID}`}
          onClick={() => handleCheckDetails(row.orderID)}
        >
          Check details
        </Button>
      ),
    },
  ];

  isMobile && columns.pop();

  const handleCheckDetails = (orderID: string) => {
    navigate(`/${orgId}/customer/order-summary/${orderID}`);
  };

  return (
    <Box>
      <Breadcrumbs
        paths={["Home", "My Orders"]}
        links={["/customer/home", "/customer/my-orders"]}
      />
      {loading ? (
        <Typography variant="h6" align="center" data-testid="loading-state">
          Loading orders...
        </Typography>
      ) : orders.length === 0 ? (
        <Box sx={EmptyContainer}>
          <img
            src={NoOrders}
            alt="No Orders"
            style={EmptyOrdersSVG}
            data-testid="no-orders-image"
          />
        </Box>
      ) : (
        <Card sx={{ p: 4 }}>
          <DataTable
            columns={columns}
            data={orders}
            emptyStateMessage="No orders available."
            rowsPerPage={5}
            rowStyle={(index) => ({
              borderBottom:
                isMobile || index === orders.length - 1
                  ? "none"
                  : "1px solid rgba(224, 224, 224, 1)",
            })}
          />
        </Card>
      )}
    </Box>
  );
};

export default MyOrders;
